import { pool, setRelayPicker } from '@nostr/gadgets/global'
import { OutboxManager } from '@nostr/gadgets/outbox'
import { NostrEvent } from '@nostr/tools/core'
import { store } from './store.service'
import { observe } from 'jotai-effect'
import { atom, getDefaultStore } from 'jotai'
import { followListAtom } from '@/atoms/lists'
import { currentPubkeyAtom } from '@/atoms/session'
import { globalism } from '@nostr/gadgets/utils'

const jotaiStore = getDefaultStore()

let globalSyncAbort: AbortController | null = null
let relayPopularityRanking: string[] = []

observe((get) => {
  let stale = false

  // tear down previous sync
  if (globalSyncAbort) {
    outbox.close()
    globalSyncAbort.abort('<account-changed>')
    globalSyncAbort = null
  }

  get(followListAtom).then(async (follows) => {
    if (stale) return

    const hardcodedPubkeys = new Set<string>()
    for (const feed of window.hallway.universe.hardcodedFeeds) {
      if (feed.pubkeys) {
        for (const pk of feed.pubkeys) hardcodedPubkeys.add(pk)
      }
    }

    const rankingPool = Array.from(new Set([...follows, ...hardcodedPubkeys]))
    if (rankingPool.length > 0) {
      const rank = await globalism(rankingPool)

      if (stale) return
      relayPopularityRanking = rank
      setRelayPicker((candidates) => {
        const urls: string[] = candidates.length === 0 ? [] : candidates.map((r) => r.url)
        if (relayPopularityRanking.length === 0) return urls.slice(0, 2)

        return [...urls]
          .sort((a, b) => relayPopularityRanking.indexOf(a) - relayPopularityRanking.indexOf(b))
          .slice(0, 2)
      })
    }

    const pubkey = get(currentPubkeyAtom)

    globalSyncAbort = new AbortController()
    const abort = globalSyncAbort

    setTimeout(() => start(pubkey || '', follows, abort.signal), 5000)
  })

  return () => {
    globalSyncAbort?.abort('<the-end>')
    stale = true
  }
})

export async function sync(
  authors: string[],
  kinds: number[],
  opts: { signal: AbortSignal }
): Promise<boolean> {
  return outbox.sync(authors, kinds, opts)
}

export let outbox: OutboxManager

export function recreateOutbox() {
  if (outbox) outbox.close()
  outbox = new OutboxManager(store, {
    pool,
    label: 'hallway',
    onsyncupdate(pubkey) {
      console.debug(':: synced updating', pubkey)
      for (let i = 0; i < current.onsync.length; i++) {
        current.onsync[i](pubkey)
      }
    },
    onbeforeupdate(_pubkey) {},
    onliveupdate(event) {
      console.debug(':: live', event)
      for (let i = 0; i < current.onnew.length; i++) {
        current.onnew[i](event)
      }
    },
    defaultRelaysForConfusedPeople: window.hallway.universe.bigRelayUrls,
    storeRelaysSeenOn: true
  })
}

setTimeout(() => recreateOutbox(), 0)

export const status: { syncing: true; pubkey: string } | { syncing: undefined | false } = {
  syncing: undefined
}

let liveTargets: string[] = []
let startSignal: AbortSignal | undefined
let refreshTimer: ReturnType<typeof setInterval> | undefined

function resync() {
  if (!liveTargets.length) return

  resetPromises()

  outbox.close()
  syncInternal()
}

export const current: {
  onsync: Array<(pubkey?: string) => void>
  onnew: Array<(event: NostrEvent) => void>
} = { onsync: [], onnew: [] }

let isReady: () => void
let _ready: Promise<void>

function resetPromises() {
  _ready = new Promise<void>((resolve) => {
    isReady = resolve
  })
}

resetPromises()

export async function ready(): Promise<void> {
  return _ready
}

export type TOutboxStatus = {
  syncing: boolean
  current: number
  total: number | undefined
}

export const outboxStatusAtom = atom<TOutboxStatus>({
  syncing: false,
  current: 0,
  total: undefined
})

current.onsync.push((pubkey?: string) => {
  if (pubkey) {
    jotaiStore.set(outboxStatusAtom, (prev) => ({ ...prev, current: prev.current + 1 }))
  }
})

export async function start(account: string, followings: string[], signal: AbortSignal) {
  ;(status as Extract<typeof status, { syncing: true }>).pubkey = account

  resetPromises()

  const hardcodedPubkeys = new Set<string>()
  for (const feed of window.hallway.universe.hardcodedFeeds) {
    if (feed.pubkeys) {
      for (const pk of feed.pubkeys) hardcodedPubkeys.add(pk)
    }
  }
  const known = new Set<string>()
  if (account) known.add(account)
  for (const pk of followings) known.add(pk)
  for (const pk of hardcodedPubkeys) known.add(pk)

  liveTargets = Array.from(known)
  startSignal = signal

  clearInterval(refreshTimer)
  syncInternal()
  refreshTimer = setInterval(resync, 1000 * 60 * 30 /* 30 minutes */)
}

async function syncInternal() {
  startSignal!.onabort = () => {
    status.syncing = undefined
    jotaiStore.set(outboxStatusAtom, (prev) => ({ ...prev, syncing: false }))
  }

  let syncTargets = liveTargets
  if (liveTargets.length > 100) {
    const now = Math.floor(Date.now() / 1000)
    const fullAbandonDays = 60
    const feedKinds = window.hallway.universe.feedKinds

    const keep = await Promise.all(
      liveTargets.map(async (pubkey) => {
        const events = await store.queryEvents({ authors: [pubkey], kinds: feedKinds }, 1)
        if (events.length === 0) return true
        const ageDays = (now - events[0].created_at) / 86400
        if (ageDays <= 3) return true
        const p = Math.min(0.9, (ageDays - 3) / (fullAbandonDays - 3))
        const skip = Math.random() < p
        if (skip) console.debug(':: outbox skip innactive target', pubkey, `${ageDays.toFixed(1)}d`)
        return !skip
      })
    )
    syncTargets = liveTargets.filter((_, i) => keep[i])
  }

  status.syncing = true
  jotaiStore.set(outboxStatusAtom, {
    syncing: true,
    current: liveTargets.length - syncTargets.length,
    total: liveTargets.length
  })

  if (0 === (await store.queryEvents({}, 1)).length) {
    // this means the database has no events.
    // let's wait some time to do our first sync, as the user right now is likely to
    // be doing the preliminary fallback query and we don't want to interfere with it
    await new Promise((resolve) => setTimeout(resolve, 15000))
  }

  const hasNew = await outbox.sync(syncTargets, window.hallway.universe.feedKinds, {
    signal: startSignal!
  })

  if (hasNew) {
    for (let i = 0; i < current.onsync.length; i++) {
      current.onsync[i]()
    }
  }

  status.syncing = false
  jotaiStore.set(outboxStatusAtom, (prev) => ({ ...prev, syncing: false }))
  isReady()
}
